import { Pin, Bell, Archive, ArchiveRestore, Trash2, Palette, RotateCcw, X, Tag, Share2, Lock, Paperclip, ClipboardCopy } from "lucide-react";
import { cn } from "@/lib/utils";
import { colorClass } from "@/lib/noteColors";
import { ColorPicker } from "@/components/QuickCreate";
import { highlightText } from "@/lib/markdown";
import { sanitizeHtml, markdownToHtml } from "@/lib/richtext";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { labelStyle } from "@/lib/labelColors";

export default function NoteCard({ note, labels, onUpdate, onTrash, onRestore, onPermanentDelete, onOpen, onShare, view, highlight }) {
  const inTrash = view === "trash";
  const inArchive = view === "archive";
  // Only make the card HTML5-draggable on devices with a fine pointer
  // (desktop/laptop with a mouse or trackpad). On phones/tablets the
  // `draggable` attribute makes iOS Safari treat the tap as the start of a
  // drag gesture and the card never fires `onClick`, so the user couldn't
  // open any note. Also HTML5 DnD doesn't fire on touch anyway.
  const canDrag = typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className={cn(
        "group relative rounded-2xl border shadow-sm hover:shadow-md transition-all paper-grain",
        "hover:-translate-y-0.5 duration-200 cursor-pointer",
        colorClass(note.color)
      )}
      onClick={() => !inTrash && onOpen(note)}
      // Desktop drag-and-drop: card can be dragged onto Sidebar targets
      // (Archive / Trash / Reminders / a label). Mobile keeps regular tap
      // interactions — HTML5 DnD is pointer-based so touch never fires it.
      draggable={canDrag}
      onDragStart={canDrag ? (e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/x-mango-note-id", note.id);
        // Also set plain text for the "already in trash" case — the sidebar
        // reads it as a hint but never a value you can get back out of the
        // dataTransfer during dragover.
        e.dataTransfer.setData("text/plain", note.id);
        try { document.body.dataset.mangoDragging = note.id; } catch {}
      } : undefined}
      onDragEnd={canDrag ? () => { try { delete document.body.dataset.mangoDragging; } catch {} } : undefined}
      data-testid={`note-card-${note.id}`}
    >
      {!inTrash && (
        <button
          onClick={(e) => { e.stopPropagation(); onUpdate(note.id, { pinned: !note.pinned }); }}
          className={cn(
            "absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center rounded-full transition-all",
            "hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
            note.pinned
              ? "opacity-100 text-amber-700 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-900/40 shadow-sm"
              : "opacity-40 group-hover:opacity-100 text-stone-500 dark:text-stone-400"
          )}
          data-testid={`note-pin-${note.id}`}
          aria-label={note.pinned ? "Unpin" : "Pin"}
          title={note.pinned ? "Unpin" : "Pin"}
        >
          <Pin className={cn("h-4 w-4", note.pinned && "fill-current")} />
        </button>
      )}

      <div className="p-4 space-y-2">
        {/* When a note is locked we hide ALL inline media so a casual
            glance can't preview images, attachment names, or voice notes
            — only the lock placeholder (rendered further down) is shown. */}
        {!note.locked && note.images?.length > 0 && (
          <div
            className={cn(
              "rounded-xl overflow-hidden -mt-1 -mx-1 grid gap-0.5",
              note.images.length === 1 && "grid-cols-1",
              note.images.length === 2 && "grid-cols-2",
              note.images.length >= 3 && "grid-cols-2"
            )}
            data-testid={`note-images-${note.id}`}
          >
            {note.images.slice(0, 4).map((p, i) => (
              <div key={p} className={cn(
                "relative aspect-video bg-stone-100 dark:bg-stone-800 overflow-hidden",
                note.images.length === 3 && i === 0 && "col-span-2"
              )}>
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy" />
                {i === 3 && note.images.length > 4 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-display text-xl">
                    +{note.images.length - 4}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!note.locked && note.attachments?.length > 0 && (
          <div className="flex flex-col gap-1.5" data-testid={`note-attachments-${note.id}`}>
            {/* Audio attachments get an inline player + optional transcript
                on the card so a tap plays the voice note AND the transcript
                is skimmable without opening the editor. */}
            {note.attachments.filter((a) => (a.content_type || "").startsWith("audio/")).slice(0, 2).map((a) => (
              <div key={a.path} className="rounded-xl bg-stone-100 dark:bg-stone-800/60 p-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                <audio
                  controls
                  src={fileUrl(a.path)}
                  className="w-full h-9"
                  data-testid={`note-audio-${note.id}`}
                />
                {a.transcript && (
                  <div className="flex items-start gap-1.5 text-xs text-stone-600 dark:text-stone-300">
                    <p className="flex-1 italic leading-relaxed line-clamp-3">
                      "{a.transcript}"
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard?.writeText(a.transcript);
                        toast.success("Transcript copied");
                      }}
                      className="h-6 w-6 inline-flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-200 dark:hover:bg-stone-700 shrink-0"
                      aria-label="Copy transcript"
                      data-testid={`copy-transcript-${note.id}`}
                    >
                      <ClipboardCopy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Non-audio: compact chip row with filename + paperclip. */}
            {note.attachments.filter((a) => !(a.content_type || "").startsWith("audio/")).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {note.attachments.filter((a) => !(a.content_type || "").startsWith("audio/")).slice(0, 3).map((a) => (
                  <span key={a.path} className="inline-flex items-center gap-1 rounded-full bg-stone-200/60 dark:bg-stone-700/60 text-stone-700 dark:text-stone-200 text-xs px-2 py-0.5 max-w-[160px]">
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <span className="truncate">{a.filename}</span>
                  </span>
                ))}
                {note.attachments.filter((a) => !(a.content_type || "").startsWith("audio/")).length > 3 && (
                  <span className="text-xs text-stone-500 self-center">+{note.attachments.filter((a) => !(a.content_type || "").startsWith("audio/")).length - 3}</span>
                )}
              </div>
            )}
          </div>
        )}

        {note.title && (
          <h3 className="font-display font-semibold text-base leading-tight pr-8 break-words" data-testid={`note-title-${note.id}`}>
            {highlightText(note.title, highlight)}
          </h3>
        )}

        {note.is_checklist ? (
          <ul className="space-y-1" data-testid={`note-checklist-${note.id}`}>
            {note.checklist_items.slice(0, 8).map((it) => (
              <li key={it.id} className="flex items-start gap-2 text-sm">
                <span
                  className={cn(
                    "mt-0.5 h-3.5 w-3.5 rounded border border-stone-400 dark:border-stone-500 inline-flex items-center justify-center shrink-0",
                    it.checked && "bg-stone-900 border-stone-900 dark:bg-stone-100 dark:border-stone-100"
                  )}
                >
                  {it.checked && <span className="text-[9px] text-white dark:text-stone-900">✓</span>}
                </span>
                <span className={cn("break-words", it.checked && "line-through text-stone-400")}>{highlightText(it.text, highlight)}</span>
              </li>
            ))}
            {note.checklist_items.length > 8 && (
              <li className="text-xs text-stone-500">+{note.checklist_items.length - 8} more</li>
            )}
          </ul>
        ) : note.locked ? (
          <div className="flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400 italic py-6">
            <Lock className="h-4 w-4" />
            <span>Password-protected</span>
          </div>
        ) : (
          note.content && (
            <div
              className="richtext-view text-sm leading-relaxed break-words line-clamp-[12]"
              data-testid={`note-content-${note.id}`}
              dangerouslySetInnerHTML={{ __html: withHighlight(sanitizeHtml(markdownToHtml(note.content)), highlight) }}
            />
          )
        )}

        {(note.labels?.length > 0 || note.reminder) && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {note.reminder && (
              <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-[11px] font-medium text-stone-700 dark:text-stone-300">
                <Bell className="h-3 w-3" />
                {safeDate(note.reminder)}
              </span>
            )}
            {note.labels?.map((l) => (
              <span key={l} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${labelStyle(labels, l).chip}`} data-testid={`note-label-chip-${l}`}>
                <Tag className="h-3 w-3" /> {l}
              </span>
            ))}
          </div>
        )}
      </div>

      <div
        className={cn(
          // Desktop: icons stay hidden (opacity-0) until the card is hovered,
          // revealed via group-hover. On touch devices (which never hover)
          // this leaves an INVISIBLE but tap-live row — users were accidentally
          // trashing notes by tapping where the trash icon lived. Fix: on
          // "no hover" devices (phones/tablets), make the icons permanently
          // visible so people can see what they're tapping.
          "flex items-center justify-end gap-0.5 px-2 pb-2 transition-opacity",
          "opacity-0 group-hover:opacity-100",
          "[@media(hover:none)]:opacity-100"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {inTrash ? (
          <>
            <IconBtn onClick={() => onRestore(note.id)} label="Restore" testid={`note-restore-${note.id}`}><RotateCcw className="h-4 w-4" /></IconBtn>
            <IconBtn onClick={() => onPermanentDelete(note.id)} label="Delete forever" testid={`note-delete-forever-${note.id}`}><X className="h-4 w-4" /></IconBtn>
          </>
        ) : (
          <>
            <ColorPicker current={note.color} onPick={(k) => onUpdate(note.id, { color: k })} />
            <LabelMenu note={note} labels={labels} onUpdate={onUpdate} />
            <IconBtn onClick={() => onShare?.(note)} label="Share" testid={`note-share-${note.id}`}>
              <Share2 className="h-4 w-4" />
            </IconBtn>
            {inArchive ? (
              <IconBtn onClick={() => onUpdate(note.id, { archived: false })} label="Unarchive" testid={`note-unarchive-${note.id}`}>
                <ArchiveRestore className="h-4 w-4" />
              </IconBtn>
            ) : (
              <IconBtn onClick={() => onUpdate(note.id, { archived: true })} label="Archive" testid={`note-archive-${note.id}`}>
                <Archive className="h-4 w-4" />
              </IconBtn>
            )}
            <IconBtn onClick={() => onTrash(note.id)} label="Trash" testid={`note-trash-${note.id}`}>
              <Trash2 className="h-4 w-4" />
            </IconBtn>
          </>
        )}
      </div>
    </motion.div>
  );
}

function IconBtn({ children, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      data-testid={testid}
      className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
    >
      {children}
    </button>
  );
}

function LabelMenu({ note, labels, onUpdate }) {
  const toggle = (name) => {
    const has = note.labels?.includes(name);
    const next = has ? note.labels.filter((n) => n !== name) : [...(note.labels || []), name];
    onUpdate(note.id, { labels: next });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
          aria-label="Labels"
          data-testid={`note-labels-${note.id}`}
        >
          <Tag className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Labels</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {labels.length === 0 && (
          <div className="px-2 py-1 text-xs text-stone-500">No labels — create one from sidebar</div>
        )}
        {labels.map((l) => (
          <DropdownMenuCheckboxItem
            key={l.id}
            checked={note.labels?.includes(l.name)}
            onCheckedChange={() => toggle(l.name)}
            onSelect={(e) => e.preventDefault()}
            data-testid={`note-label-toggle-${l.name}`}
          >
            {l.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function safeDate(iso) {
  try { return format(new Date(iso), "MMM d"); } catch { return ""; }
}

/** Wrap plain-text occurrences of `needle` inside an HTML string with <mark>.
 *  Uses safe DOM APIs — no innerHTML assignment with user text — so an
 *  (already-sanitized) note that happens to contain literal "<script>" as
 *  text would never be re-parsed as HTML during highlighting. */
function withHighlight(html, needle) {
  if (!html || !needle) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstChild;
    const re = new RegExp(`(${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
    const walk = (node) => {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === 3) {
          const text = child.nodeValue;
          if (!re.test(text)) continue;
          re.lastIndex = 0;
          const frag = doc.createDocumentFragment();
          let last = 0;
          let m;
          while ((m = re.exec(text)) !== null) {
            if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)));
            const mark = doc.createElement("mark");
            mark.textContent = m[0];
            frag.appendChild(mark);
            last = m.index + m[0].length;
          }
          if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== "MARK") {
          walk(child);
        }
      }
    };
    walk(root);
    return root.innerHTML;
  } catch { return html; }
}
